import { MainButton } from "@/Components/MainButton"
import { setModalData } from "@/store/globalSlice"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { getUserCreds, isMobile, socket } from "@/utils/utils"
import { NEW_PLAYER_GAMES_THRESHOLD } from "@mono/constants"

// The proposal button wears the same MainButton skin, and the same violet, as
// the turn buttons in GameButtons — it is one more thing you press, so it looks
// like the other things you press. The badge deliberately does not: see below.
const FAST_PACE_COLOR = "#8b5cf6"

/**
 * The pace of the game, top-left of the board — the mirror of the
 * settings/pause/leave cluster in the opposite corner.
 *
 * It is one control in two states. While the game is running normally it is the
 * button that puts fast pace to the table; once the table has agreed it becomes
 * the badge saying so, and stays there. That is deliberate: the switch is
 * one-way and permanent for the rest of the game, so the thing that made it
 * happen should turn into the record of it rather than disappearing and leaving
 * players wondering why their turns suddenly got shorter.
 *
 * While a vote is open this stands down entirely and FastPaceVotePanel takes
 * the slot — the panel already says a vote is underway, and two cards stacked
 * in this corner would reach down over the top player card. Nothing to press
 * means the proposal cannot be raised twice either.
 */
const FastPaceControl: React.FC = () => {
  const dispatch = useAppDispatch()
  const gameId = useAppSelector((state) => state.global.myGame?.id)
  const isQuickGame = useAppSelector(
    (state) => !!state.global.myGame?.settings?.isQuickGame
  )
  const gameOver = useAppSelector(
    (state) => !!state.global.myGame?.situation?.gameOver
  )
  const voteInProgress = useAppSelector(
    (state) => !!state.global.myGame?.situation?.fastPaceVote
  )
  // Only somebody actually playing gets to ask for it: a bankrupt seat has no
  // turns left to shorten, and a spectator was never at the table.
  const isPlaying = useAppSelector((state) =>
    Boolean(
      state.global.myGame?.situation?.players.find(
        (player) => player.id === getUserCreds().email && !player.isBankrupt
      )
    )
  )
  // Is there anybody left to hold a vote for? Mirrors the server's own test
  // (fastPaceHelpers.hasAnotherHumanPlayer, down to counting a "bot" in the id
  // as a bot), because it is the answer to that question that decides whether
  // this click opens a vote or switches the room over on the spot.
  const isBotsOnlyGame = useAppSelector(
    (state) =>
      !state.global.myGame?.situation?.players.some(
        (player) =>
          !player.isBankrupt &&
          player.id !== getUserCreds().email &&
          !player.bot &&
          !player.id?.includes?.("bot")
      )
  )
  // Played-games count off the account. It lives flat on the user document and
  // nested on the seeded ones, hence both reads; an account that never loaded
  // simply is not treated as new, since warning a veteran on every click is a
  // worse failure than not warning a newcomer once.
  const gamesPlayed = useAppSelector(
    (state) =>
      state.global.userData?.gamesPlayed ??
      state.global.userData?.stats?.gamesPlayed
  )
  const isNewPlayer =
    gamesPlayed !== undefined && gamesPlayed < NEW_PLAYER_GAMES_THRESHOLD

  // A table of bots has nobody to ask, so the server acts on the click itself
  // rather than opening a vote — no 45 seconds to reconsider in, and nobody
  // else's "no" to fall back on. For a player with a handful of games behind
  // them that is worth a sentence first: what fast pace does, and the one thing
  // it stops doing (see FastPaceNewPlayerConfirmModal, which sends the
  // proposal itself once they say yes).
  const handleProposeFastPace = () => {
    if (isBotsOnlyGame && isNewPlayer) {
      dispatch(setModalData({ fastPaceNewPlayerConfirm: { visible: true } }))
      return
    }
    socket.emit("game.propose-fast-pace", gameId || "")
  }

  if (gameOver) return null

  if (isQuickGame) {
    // A label, not a control. It is the standing record of a decision the table
    // already took — the switch is one-way and there is nothing left to press —
    // so it drops the button skin entirely: no raised body, no shadow, no hover
    // or press response, and a tinted pill sized only to its own words. It does
    // keep the h-8 of the Help key it shares the row with, though: the two sit
    // on one line, and a pill half a step shorter than its neighbour reads as a
    // misalignment rather than as a different kind of thing.
    return (
      <div
        className="inline-flex h-8 flex-row items-center gap-2 rounded-full border border-violet-400/60 bg-violet-600/80 px-3"
        data-testid="game.fast-pace-badge"
        title="This game is being played fast paced — shorter timers, quicker animations"
      >
        <i className="fa-solid fa-bolt text-violet-200 text-xs" />
        <span className="text-xs text-violet-100 font-poppins font-semibold tracking-wide uppercase">
          Fast paced
        </span>
      </div>
    )
  }

  // The panel that replaces it is shown to everyone, playing or not, so this
  // check comes after the vote one — a bankrupt seat with no button still sees
  // what the rest of the table is deciding.
  if (voteInProgress) return null

  if (!isPlaying) return null

  return (
    // Small and only as wide as its words. It is an optional aside — nothing
    // the game is waiting on — so it sits quietly at the head of the column
    // rather than claiming the width of a player card.
    //
    // On a phone that restraint goes too far: at this size the key is a hard
    // target for a thumb, and it has the row to itself there (the Help button
    // is desktop-only), so it grows a step — taller body, roomier sides and
    // bigger type — without crowding anything.
    <MainButton
      baseColor={FAST_PACE_COLOR}
      size="sm"
      onClick={handleProposeFastPace}
      className={
        isMobile() ? "!h-11 !min-w-0 !px-4" : "!h-8 !min-w-0 !px-3"
      }
      data-testid="game.propose-fast-pace-button"
      aria-label="Make game fast pace"
      title="Ask everyone to switch to fast pace — shorter timers and quicker animations"
    >
      <div className="flex flex-row items-center gap-2">
        <i
          className={`fa-solid fa-bolt text-white ${
            isMobile() ? "text-sm" : "text-xs"
          }`}
        />
        <span
          className={`text-white/90 font-poppins font-semibold tracking-wide uppercase ${
            isMobile() ? "text-sm" : "text-xs"
          }`}
        >
          Make game fast pace
        </span>
      </div>
    </MainButton>
  )
}

export default FastPaceControl
